// Parcelas dos acordos da ficha, um bloco por acordo: as em aberto primeiro (as pagas só com "Mostrar pagas")
import React from "react";
import { Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { coresDoTema, FONTE } from "assets/coresTema";
import { dataBR, corDaSituacao, SITUACAO_BOLETO } from "./baixaUtils";

const COLUNAS = ["", "Parcela", "Vencimento", "Valor", "Saldo", "Boleto", "Situação"];

export default function TabelaParcelas({ acordos, mostrarPagas, selecionados, onAlternar, onMarcarAcordo }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);

    const visiveis = (acordos || []).filter(a => mostrarPagas || a.emAberto > 0);
    if (visiveis.length === 0) {
        return (
            <MDBox py={4} textAlign="center">
                <MDTypography variant="button" color="text">
                    {(acordos || []).length === 0
                        ? "Esta ficha não tem acordo."
                        : "Nenhuma parcela em aberto. Ligue \"Mostrar pagas\" para ver as já baixadas."}
                </MDTypography>
            </MDBox>
        );
    }

    return visiveis.map(acordo => {
        const linhas = acordo.parcelas.filter(p => mostrarPagas || p.emAberto);
        const abertas = acordo.parcelas.filter(p => p.emAberto);
        const todasMarcadas = abertas.length > 0 && abertas.every(p => selecionados[p.id]);
        return (
            <MDBox key={acordo.id} mb={2} sx={{ border: `1px solid ${cores.borda}`, borderRadius: 2, overflow: "hidden" }}>
                <MDBox px={2} py={1} display="flex" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ backgroundColor: cores.painel }}>
                    <MDTypography variant="button" fontWeight="bold">Acordo #{acordo.id}</MDTypography>
                    <Chip size="small" variant="outlined" label={acordo.situacao}
                        color={acordo.situacao === "Quitado" ? "success" : acordo.situacao === "Quebrado" ? "warning" : "info"} />
                    {acordo.acordoPagamento && (
                        <Tooltip title="Pagamento avulso registrado como acordo (comissão do negociador): não prende o débito; o estorno cancela o acordo e devolve o valor ao saldo.">
                            <Chip size="small" variant="outlined" color="info" label="Acordo de pagamento" />
                        </Tooltip>
                    )}
                    <MDTypography variant="caption" color="text" sx={{ flex: 1, minWidth: 220 }}>
                        Formalizado em {dataBR(acordo.formalizacao)} · {toCurrency(acordo.valor)} · {acordo.pagas} de {acordo.qtdParcelas} pagas
                        {acordo.negociador ? ` · ${acordo.negociador}` : ""}
                    </MDTypography>
                    {abertas.length > 1 && (
                        <MDButton size="small" variant="text" color="info" onClick={() => onMarcarAcordo(acordo, !todasMarcadas)}>
                            {todasMarcadas ? "Desmarcar todas" : `Marcar as ${abertas.length} em aberto`}
                        </MDButton>
                    )}
                </MDBox>
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 560 }}>
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                                {COLUNAS.map((c, i) => (
                                    <TableCell key={i} align={i === 3 || i === 4 ? "right" : "left"}
                                        sx={{ py: 0.75, px: 1, width: i === 0 ? 40 : undefined, backgroundColor: cores.painelEscuro }}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ fontSize: FONTE.cabecalho }}>{c}</MDTypography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {linhas.map(p => {
                                const marcada = Boolean(selecionados[p.id]);
                                return (
                                    <TableRow key={p.id} hover={p.emAberto}
                                        onClick={() => p.emAberto && onAlternar(p, acordo)}
                                        sx={{ cursor: p.emAberto ? "pointer" : "default", backgroundColor: marcada ? cores.hover : undefined,
                                            opacity: p.emAberto ? 1 : 0.75 }}>
                                        <TableCell sx={{ py: 0.25, px: 1 }}>
                                            {p.emAberto && (
                                                <Checkbox size="small" checked={marcada} onClick={(e) => e.stopPropagation()}
                                                    onChange={() => onAlternar(p, acordo)} inputProps={{ "aria-label": `Parcela ${p.numero}` }} />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1 }}>
                                            <MDTypography variant="button" fontWeight="medium">{p.numero}/{p.total}</MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1 }}>
                                            <MDTypography variant="button" display="block">{dataBR(p.vencimento)}</MDTypography>
                                            {p.diasAtraso != null && p.diasAtraso > 0 && (
                                                <MDTypography variant="caption" color="error" display="block">
                                                    {p.diasAtraso} dia{p.diasAtraso > 1 ? "s" : ""} em atraso
                                                </MDTypography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}><MDTypography variant="button">{toCurrency(p.valor)}</MDTypography></TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                                            <MDTypography variant="button" fontWeight={p.emAberto ? "bold" : "regular"} display="block">
                                                {p.emAberto ? toCurrency(p.saldo) : "-"}
                                            </MDTypography>
                                            {p.pago > 0 && (
                                                <MDTypography variant="caption" color="text" display="block">pago {toCurrency(p.pago)}</MDTypography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1 }}>
                                            {p.nossoNumero ? (
                                                <Tooltip title={p.boletoAtivo && p.emAberto
                                                    ? "Boleto ativo: na baixa por outra forma de pagamento ele é cancelado (pedido de baixa na próxima remessa, se já foi ao banco)."
                                                    : ""}>
                                                    <MDBox>
                                                        <MDTypography variant="caption" display="block">{p.nossoNumero}</MDTypography>
                                                        <MDTypography variant="caption" color={p.boletoAtivo ? "warning" : "text"} display="block">
                                                            {SITUACAO_BOLETO[p.situacaoBoleto] || "-"}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Tooltip>
                                            ) : <MDTypography variant="caption" color="text">-</MDTypography>}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1 }}>
                                            <Chip size="small" variant="outlined" color={corDaSituacao(p.situacao)} label={p.situacao} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MDBox>
        );
    });
}
